/**
 * PlanFeaturesPage — Manage features assignable to service plans.
 */
import { useState, useEffect, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Column } from 'primereact/column'
import DataTable from '../../components/ui/DataTable.js'
import { confirmDialog } from 'primereact/confirmdialog'
import ResourceDrawer from '../../components/admin/ResourceDrawer.js'
import { Checkbox } from 'primereact/checkbox'
import {
  Tag, CheckCircle2, XCircle, Plus, ArrowLeft, Search, RefreshCw,
  Pencil, EyeOff, Eye, Trash2,
} from 'lucide-react'
import { formatInTimezone } from '../../utils/timezone.js'

interface PlanFeature {
  id: string
  name: string
  description: string | null
  enabled: boolean
  createdAt: string
}

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Tag
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div
          className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: `${accent}10` }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: accent }}
      />
    </div>
  )
}

/* ─── Action Icon ─── */
function ActionIcon({ icon: Icon, label, onClick, color }: {
  icon: typeof Pencil
  label: string
  onClick: () => void
  color: string
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 transition-all duration-150 hover:border-transparent hover:text-white"
      style={{ ['--color' as any]: color }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = (e.currentTarget as HTMLElement).style.getPropertyValue('--color')
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'
        ;(e.currentTarget as HTMLElement).style.borderColor = '#f3f4f6'
      }}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

/* ─── Status Badge ─── */
function StatusBadge({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Enabled
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Disabled
    </span>
  )
}

export default function PlanFeaturesPage(): JSX.Element {
  const navigate = useNavigate()
  const toast = useToast()
  const [features, setFeatures] = useState<PlanFeature[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create')
  const [selectedFeature, setSelectedFeature] = useState<PlanFeature | undefined>(undefined)
  const [featureName, setFeatureName] = useState('')
  const [featureDescription, setFeatureDescription] = useState('')
  const [featureEnabled, setFeatureEnabled] = useState(true)
  const [saving, setSaving] = useState(false)

  const fetchFeatures = async (): Promise<void> => {
    setLoading(true)
    try {
      const res = await api.get<{ features: PlanFeature[] }>('/platform/plans/features')
      setFeatures(res.features || [])
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Error fetching features')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchFeatures()
  }, [])

  const visible = search.trim()
    ? features.filter(f =>
        f.name.toLowerCase().includes(search.toLowerCase()) ||
        (f.description?.toLowerCase().includes(search.toLowerCase()) ?? false)
      )
    : features

  const handleSaveFeature = async (): Promise<void> => {
    if (!featureName) return
    setSaving(true)
    try {
      if (drawerMode === 'edit' && selectedFeature) {
        await api.put(`/platform/plans/features/${selectedFeature.id}`, {
          name: featureName,
          description: featureDescription || null,
          enabled: featureEnabled,
        })
        toast.success('Feature updated successfully')
      } else {
        await api.post('/platform/plans/features', {
          name: featureName,
          description: featureDescription || null,
          enabled: featureEnabled,
        })
        toast.success('Feature created successfully')
      }
      setFeatureName('')
      setFeatureDescription('')
      setFeatureEnabled(true)
      setDrawerOpen(false)
      setSelectedFeature(undefined)
      void fetchFeatures()
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Error saving feature')
    } finally {
      setSaving(false)
    }
  }

  const handleEditFeature = (feature: PlanFeature): void => {
    setDrawerMode('edit')
    setSelectedFeature(feature)
    setFeatureName(feature.name)
    setFeatureDescription(feature.description || '')
    setFeatureEnabled(feature.enabled)
    setDrawerOpen(true)
  }

  const handleToggleFeature = async (feature: PlanFeature): Promise<void> => {
    try {
      await api.put(`/platform/plans/features/${feature.id}`, { enabled: !feature.enabled })
      toast.success(`Feature ${!feature.enabled ? 'enabled' : 'disabled'}`)
      void fetchFeatures()
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Error updating feature')
    }
  }

  const handleDeleteFeature = (feature: PlanFeature): void => {
    confirmDialog({
      message: `Delete feature "${feature.name}"? This will remove it from all plans.`,
      header: `Delete ${feature.name}`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.delete(`/platform/plans/features/${feature.id}`)
          toast.success('Feature deleted successfully')
          void fetchFeatures()
        } catch (e: unknown) {
          toast.error(e instanceof Error ? e.message : 'Error deleting feature')
        }
      },
    })
  }

  const enabledCount = features.filter(f => f.enabled).length

  return (
    <div className="w-full space-y-6 px-4 pb-10">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Plan Features</h1>
          <p className="text-sm text-gray-400 mt-1">Manage features that can be assigned to service plans.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/platform/plans')}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Plans
          </button>
          <button
            onClick={() => {
              setDrawerMode('create')
              setSelectedFeature(undefined)
              setFeatureName('')
              setFeatureDescription('')
              setFeatureEnabled(true)
              setDrawerOpen(true)
            }}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
          >
            <Plus className="h-4 w-4" />
            New Feature
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Features" value={features.length} icon={Tag} accent="#3454cf" />
        <StatCard label="Enabled" value={enabledCount} icon={CheckCircle2} accent="#10b981" />
        <StatCard label="Disabled" value={features.length - enabledCount} icon={XCircle} accent="#ef4444" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
          <input
            type="text"
            placeholder="Search features by name or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 focus:outline-none transition-all"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 font-medium">{visible.length} features</span>
          <div className="w-px h-4 bg-gray-200" />
          <button
            onClick={() => { setSearch(''); void fetchFeatures() }}
            className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        value={visible}
        loading={loading}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
        emptyMessage="No features found."
      >
        <Column
          field="name"
          header="Feature Name"
          sortable
          body={(row: PlanFeature) => {
            const shortId = row.id.split('-')[0]?.substring(0, 4) || '??'
            return (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-[#8b5cf6]/5 flex items-center justify-center shrink-0">
                  <Tag className="h-5 w-5 text-[#8b5cf6]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{row.name}</p>
                  {row.description && (
                    <p className="text-xs text-gray-400 truncate max-w-[260px]">{row.description}</p>
                  )}
                  <p className="text-xs text-gray-300">ID: {shortId}</p>
                </div>
              </div>
            )
          }}
          style={{ width: '35%' }}
        />
        <Column
          field="enabled"
          header="Status"
          sortable
          body={(row: PlanFeature) => <StatusBadge enabled={row.enabled} />}
        />
        <Column
          field="createdAt"
          header="Created"
          sortable
          body={(row: PlanFeature) => (
            <span className="text-xs text-gray-400">
              {formatInTimezone(row.createdAt, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US')}
            </span>
          )}
        />
        <Column
          header="Actions"
          body={(row: PlanFeature) => (
            <div className="flex items-center gap-1">
              <ActionIcon icon={Pencil} label="Edit" onClick={() => handleEditFeature(row)} color="#3454cf" />
              <ActionIcon icon={row.enabled ? EyeOff : Eye} label={row.enabled ? 'Disable' : 'Enable'} onClick={() => handleToggleFeature(row)} color={row.enabled ? '#f6931c' : '#10b981'} />
              <ActionIcon icon={Trash2} label="Delete" onClick={() => handleDeleteFeature(row)} color="#ef4444" />
            </div>
          )}
          style={{ width: '120px' }}
        />
      </DataTable>

      {/* Create/Edit Drawer */}
      <ResourceDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setFeatureName('')
          setFeatureDescription('')
          setFeatureEnabled(true)
          setSelectedFeature(undefined)
        }}
        onSave={handleSaveFeature}
        saving={saving}
        width="wide"
        title={drawerMode === 'edit' ? 'Edit Feature' : 'New Feature'}
        disabled={!featureName.trim()}
      >
        <div className="space-y-5">
          {/* Status Section */}
          <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-50">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Configuration</h3>
            </div>
            <div className="p-5">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Status</label>
              <div
                className={`flex items-center gap-3 rounded-sm border p-4 cursor-pointer transition-all ${
                  featureEnabled
                    ? 'border-emerald-100 bg-emerald-50/30'
                    : 'border-gray-200 bg-gray-50/30'
                }`}
                onClick={() => setFeatureEnabled(!featureEnabled)}
              >
                <Checkbox
                  inputId="feature-enabled-cb"
                  checked={featureEnabled}
                  onChange={(e) => setFeatureEnabled(!!e.checked)}
                />
                <label htmlFor="feature-enabled-cb" className="text-sm font-medium text-gray-700 flex-1 cursor-pointer">
                  {featureEnabled ? (
                    <span className="text-emerald-700 font-semibold">Enabled</span>
                  ) : (
                    <span className="text-gray-500">Disabled</span>
                  )}
                  <span className="block text-xs text-gray-400 mt-0.5">
                    {featureEnabled
                      ? 'Feature is available for plan assignment'
                      : 'Feature is hidden from plan configuration'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Identity Section */}
          <div className="bg-white rounded-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-50">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Feature Identity</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Feature Name <span className="text-red-500">*</span>
                </label>
                <InputText
                  value={featureName}
                  onChange={(e) => setFeatureName(e.target.value)}
                  placeholder="e.g., IVR Menu, Call Analytics"
                  className="w-full"
                />
                <p className="text-xs text-gray-400 mt-1">Unique identifier shown in plan configuration.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                <InputTextarea
                  value={featureDescription}
                  onChange={(e) => setFeatureDescription(e.target.value)}
                  placeholder="Describe what this feature enables..."
                  rows={3}
                  className="w-full"
                />
                <p className="text-xs text-gray-400 mt-1">Optional description for internal reference.</p>
              </div>
            </div>
          </div>
        </div>
      </ResourceDrawer>

    </div>
  )
}
